import { useId } from 'react';
import { Alert, Button } from '@backstage/ui';
import { useTranslationRef } from '@backstage/frontend-plugin-api';
import {
  type BranchReadiness,
  type ReadinessContext,
} from '@internal/plugin-waadoo-core';
import { isShellPod } from './odooPods';
import { odooTranslationRef } from './translation';
import { InitReportCard } from './InitReportCard';
import { initReportPhase } from './initReport';
import { useInitReport } from './useInitReport';

/**
 * La disponibilité d'Odoo sur la page d'une branche (`BranchReadiness`),
 * déplacée de BranchPage.tsx à l'étape 9d, au même endroit de ses hooks.
 *
 * THE INIT JOB'S REPORT (useInitReport.ts): read here, once, for the
 * overview's « Modules » card and its banner — polled while a run is in
 * progress, which both follow. Read once the overview has been opened,
 * like the cards of every tab (`enabled`); again on a new deployment
 * learnt of (`recheckKey`, the follow-up's `onChange` of the page), which
 * may have started a new init job.
 */
export function useOdooReadiness(context: ReadinessContext): BranchReadiness {
  const { project, namespace, generation, recheckKey, enabled, pods, now } =
    context;
  const initReport = useInitReport({
    project,
    namespace,
    generation,
    refreshKey: recheckKey,
    enabled,
  });
  // The card's id, what the banner's « Voir les modules » scrolls to.
  const initReportId = useId();
  const { t } = useTranslationRef(odooTranslationRef);
  // One instant for the card and the banner (`now`, the page's): the two
  // must never disagree on whether a run is still in progress
  // (`INIT_REPORT_STALE_MS`).
  const initPhase = initReport ? initReportPhase(initReport, now) : undefined;
  // The job's pod, while the tree lists it — running: `extractPods` drops a
  // terminated pod, and the Job is deleted 10 min after its end. The tree is
  // read again by the pods' follow-up (`podsFollowUp.ts`: a change of the
  // Application, a return to the tab, every 10 s while a pod is not
  // settled): a job started after the load gets its link with the next read
  // that lists its pod.
  const initPodListed =
    initReport?.podName !== undefined &&
    pods.some(pod => pod.name === initReport.podName);
  const initLogsHref =
    initPodListed && initReport?.podName
      ? context.podLogsHref(initReport.podName)
      : undefined;
  // « Odoo démarre » when an application pod's init containers run — the
  // Odoo chart's wait-db / wait-init: kubectl's `Init:<n>/<total>` status
  // reason, as ArgoCD repeats it. The init job's own pod is an `app` pod
  // too (categorizePod): while it waits for the database, that is Odoo
  // waiting as well. Not the Odoo shell's pod: it starts on request, and
  // the environment's Odoo is not waiting for it.
  const odooStarting = pods.some(
    pod =>
      pod.category === 'app' &&
      !isShellPod(pod) &&
      pod.statusReason?.startsWith('Init:'),
  );
  let initBanner: 'running' | 'starting' | undefined;
  if (initPhase === 'running') {
    initBanner = 'running';
  } else if (odooStarting) {
    initBanner = 'starting';
  }
  const showInitReport = () => {
    const card = document.getElementById(initReportId);
    // Optional: jsdom, which the tests render in, has no scrollIntoView.
    card?.scrollIntoView?.({ block: 'start' });
    card?.focus({ preventScroll: true });
  };

  return {
    // Odoo unavailable for a while, said first: the init job is updating
    // the modules (its report says it runs), or an Odoo pod waits in its
    // init containers. Info, not warning: it is the normal course of a
    // deployment. The link only with a card to go to.
    banner: initBanner && (
      <Alert
        className="waadoo-branch-banner"
        status="info"
        icon
        title={t(
          initBanner === 'running'
            ? 'initReport.banner.running'
            : 'initReport.banner.starting',
        )}
        customActions={
          initReport ? (
            <Button variant="tertiary" size="small" onPress={showInitReport}>
              {t('initReport.banner.showModules')}
            </Button>
          ) : undefined
        }
      />
    ),
    // « Modules », the report of the last init job: nothing without a
    // report (charts before 0.9.6). Read above, not by the card: Refresh
    // reaches it through `generation`.
    card: namespace && initReport && (
      <InitReportCard
        id={initReportId}
        report={initReport}
        now={now}
        logsHref={initLogsHref}
      />
    ),
    // « Voir les modules » dans le bandeau d'échec du déploiement, quand le
    // Job d'init est en cause (la page en décide) et qu'il y a une carte
    // où aller.
    syncFailureAction: initReport ? (
      <Button variant="tertiary" size="small" onPress={showInitReport}>
        {t('initReport.banner.showModules')}
      </Button>
    ) : undefined,
  };
}
